"use client";

import {
  isGA4Ready,
  pushGA4Event,
  setGA4UserData,
  type GA4UserData,
} from "~/lib/ga4-browser";
import {
  COOKIE_CONSENT_STORAGE_KEY,
  parseStoredConsent,
} from "~/lib/consent-region";

export type Ga4PurchaseEventName = "purchase" | "repeat_session_booking";

export interface Ga4PurchaseItem {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
}

export interface Ga4PurchaseParams {
  [key: string]: unknown;
  transaction_id: string;
  value: number;
  currency: string;
  items: Ga4PurchaseItem[];
}

interface StoredGa4PurchaseEvent {
  eventName: Ga4PurchaseEventName;
  params: Ga4PurchaseParams;
  userData?: GA4UserData;
}

const PENDING_STORAGE_KEY = "ga4_pending_purchase_events_v1";
const SENT_STORAGE_KEY = "ga4_sent_purchase_transactions_v1";
const MAX_SENT_TRANSACTION_IDS = 100;
const MAX_PENDING_PURCHASE_EVENTS = 25;

export function resolveGa4PurchaseEventName(
  value: string | null | undefined,
): Ga4PurchaseEventName | null {
  if (value == null || value === "" || value === "purchase") {
    return "purchase";
  }

  if (value === "repeat_purchase") {
    return "repeat_session_booking";
  }

  return null;
}

function canUseStorage(): boolean {
  return (
    typeof window !== "undefined" && typeof window.localStorage !== "undefined"
  );
}

function readJson<T>(key: string, fallback: T): T {
  if (!canUseStorage()) {
    return fallback;
  }

  const rawValue = window.localStorage.getItem(key);
  if (!rawValue) {
    return fallback;
  }

  try {
    return JSON.parse(rawValue) as T;
  } catch {
    return fallback;
  }
}

function writeJson<T>(key: string, value: T): boolean {
  if (!canUseStorage()) {
    return false;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

function readPendingPurchaseEvents(): Record<string, StoredGa4PurchaseEvent> {
  return readJson<Record<string, StoredGa4PurchaseEvent>>(
    PENDING_STORAGE_KEY,
    {},
  );
}

function writePendingPurchaseEvents(
  pendingEvents: Record<string, StoredGa4PurchaseEvent>,
): boolean {
  return writeJson(PENDING_STORAGE_KEY, pendingEvents);
}

function readSentTransactionIds(): string[] {
  return readJson<string[]>(SENT_STORAGE_KEY, []);
}

function writeSentTransactionIds(transactionIds: string[]): boolean {
  return writeJson(
    SENT_STORAGE_KEY,
    transactionIds.slice(-MAX_SENT_TRANSACTION_IDS),
  );
}

function hasAdUserDataConsent(): boolean {
  if (!canUseStorage()) {
    return false;
  }

  return (
    parseStoredConsent(window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY))
      ?.adConsent ?? false
  );
}

function removePendingUserDataWithoutConsent(
  pendingEvents: Record<string, StoredGa4PurchaseEvent>,
  adUserDataConsent: boolean,
): boolean {
  if (adUserDataConsent) {
    return false;
  }

  let didChange = false;
  for (const pendingEvent of Object.values(pendingEvents)) {
    if (!pendingEvent.userData) {
      continue;
    }
    delete pendingEvent.userData;
    didChange = true;
  }

  return didChange;
}

function trimPendingPurchaseEvents(
  pendingEvents: Record<string, StoredGa4PurchaseEvent>,
): boolean {
  const transactionIds = Object.keys(pendingEvents);
  if (transactionIds.length <= MAX_PENDING_PURCHASE_EVENTS) {
    return false;
  }

  const overflowCount = transactionIds.length - MAX_PENDING_PURCHASE_EVENTS;
  for (const transactionId of transactionIds.slice(0, overflowCount)) {
    delete pendingEvents[transactionId];
  }

  return true;
}

export function trackReliableGA4PurchaseEvent(
  eventName: Ga4PurchaseEventName,
  params: Ga4PurchaseParams,
  userData?: GA4UserData,
): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  if (
    !params.transaction_id ||
    !Number.isFinite(params.value) ||
    params.value <= 0
  ) {
    return false;
  }

  const sentTransactionIds = new Set(readSentTransactionIds());
  if (sentTransactionIds.has(params.transaction_id)) {
    return true;
  }

  const adUserDataConsent = hasAdUserDataConsent();
  const consentedUserData = adUserDataConsent ? userData : undefined;

  if (isGA4Ready()) {
    if (consentedUserData) {
      setGA4UserData(consentedUserData);
    }

    const pushed = pushGA4Event(eventName, params);
    if (!pushed) {
      return false;
    }

    sentTransactionIds.add(params.transaction_id);
    writeSentTransactionIds(Array.from(sentTransactionIds));
    return true;
  }

  const pendingEvents = readPendingPurchaseEvents();
  let didChangeStoredState = removePendingUserDataWithoutConsent(
    pendingEvents,
    adUserDataConsent,
  );
  pendingEvents[params.transaction_id] = {
    eventName,
    params,
    userData: consentedUserData,
  };
  didChangeStoredState = true;
  didChangeStoredState = trimPendingPurchaseEvents(pendingEvents) || didChangeStoredState;
  if (!writePendingPurchaseEvents(pendingEvents) && didChangeStoredState) {
    return false;
  }

  flushPendingGA4PurchaseEvents();
  return true;
}

export function flushPendingGA4PurchaseEvents(): boolean {
  const pendingEvents = readPendingPurchaseEvents();
  const sentTransactionIds = new Set(readSentTransactionIds());
  const adUserDataConsent = hasAdUserDataConsent();
  let didFlushAnyEvents = false;
  let didChangeStoredState =
    removePendingUserDataWithoutConsent(pendingEvents, adUserDataConsent) ||
    trimPendingPurchaseEvents(pendingEvents);

  if (!isGA4Ready()) {
    if (didChangeStoredState) {
      writePendingPurchaseEvents(pendingEvents);
    }
    return false;
  }

  for (const [transactionId, pendingEvent] of Object.entries(pendingEvents)) {
    if (sentTransactionIds.has(transactionId)) {
      delete pendingEvents[transactionId];
      didChangeStoredState = true;
      continue;
    }

    if (pendingEvent.userData && adUserDataConsent) {
      setGA4UserData(pendingEvent.userData);
    }

    const pushed = pushGA4Event(pendingEvent.eventName, pendingEvent.params);
    if (!pushed) {
      continue;
    }

    sentTransactionIds.add(transactionId);
    delete pendingEvents[transactionId];
    didFlushAnyEvents = true;
    didChangeStoredState = true;
  }

  if (didChangeStoredState) {
    writePendingPurchaseEvents(pendingEvents);
    writeSentTransactionIds(Array.from(sentTransactionIds));
  }

  return didFlushAnyEvents;
}

export function resetGa4PurchaseStateForTests(): void {
  if (!canUseStorage()) {
    return;
  }

  window.localStorage.removeItem(PENDING_STORAGE_KEY);
  window.localStorage.removeItem(SENT_STORAGE_KEY);
}
